import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    AlertCircle,
    Calendar,
    Stethoscope,
    Clock
} from 'lucide-react';
import { Encounter } from '../../types/encounter';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/authUtils';
import { UserAvatar } from '../common/UserAvatar';

interface EncountersListProps {
    petId: string;
    petOwnerId?: string;
    showHeader?: boolean;
    maxItems?: number;
}

const EncountersList: React.FC<EncountersListProps> = ({
    petId,
    petOwnerId,
    showHeader = true,
    maxItems
}) => {
    const [encounters, setEncounters] = useState<Encounter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchEncounters();
    }, [petId]);

    const fetchEncounters = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(API_ENDPOINTS.ENCOUNTERS.BY_PET(petId), {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch encounters: ${response.statusText}`);
            }

            const data = await response.json();
            let encounterList = Array.isArray(data) ? data : [];

            // Sort by appointment_date desc (or created_at as fallback)
            encounterList.sort((a, b) => {
                const dateA = a.appointment_date || a.created_at;
                const dateB = b.appointment_date || b.created_at;
                return new Date(dateB).getTime() - new Date(dateA).getTime();
            });

            // Apply maxItems limit if specified
            if (maxItems && encounterList.length > maxItems) {
                encounterList = encounterList.slice(0, maxItems);
            }

            setEncounters(encounterList);
        } catch (err) {
            console.error('Error fetching encounters:', err);
            setError(err instanceof Error ? err.message : 'Failed to load encounters');
        } finally {
            setLoading(false);
        }
    };

    const canCreateEncounters = () => {
        return user?.role === 'VET' || user?.role === 'ADMIN' || user?.role === 'VET_STAFF' || user?.role === 'PRACTICE_ADMIN';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Check if a date is today
    const isToday = (dateString: string): boolean => {
        const date = new Date(dateString);
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    // Get initials from a name (e.g., "Dr. John Smith" -> "JS")
    const getInitials = (name?: string): string => {
        if (!name) return '?';
        
        // Remove common prefixes like "Dr.", "Dr"
        const cleanName = name.replace(/^(Dr\.?\s*)/i, '').trim();
        
        const parts = cleanName.split(' ').filter(p => p.length > 0);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        
        // Get first letter of first and last name
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    if (loading) {
        return (
            <div className="space-y-4">
                {showHeader && (
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
                            <Stethoscope className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                            Encounters
                        </h3>
                    </div>
                )}
                <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-gray-200 dark:bg-dark-bg-tertiary h-24 rounded-none"></div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4">
                {showHeader && (
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
                            <Stethoscope className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                            Encounters
                        </h3>
                    </div>
                )}
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-none p-4">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                        <p className="text-red-700 dark:text-red-300">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {showHeader && (
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
                        <Stethoscope className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                        Encounters
                        {encounters.length > 0 && (
                            <span className="ml-2 text-sm text-gray-500 dark:text-dark-text-muted">
                                ({encounters.length} encounter{encounters.length !== 1 ? 's' : ''})
                            </span>
                        )}
                    </h3>
                    {/* We might want to add a create button here later, but usually encounters start from appointments */}
                </div>
            )}

            {encounters.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-dark-bg-tertiary rounded-none border-2 border-dashed border-gray-300 dark:border-dark-border">
                    <Stethoscope className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">No encounters</h3>
                    <p className="text-gray-600 dark:text-dark-text-muted mb-4">
                        This pet doesn't have any encounters yet.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {encounters.map((encounter) => {
                        const displayDate = encounter.appointment_date || encounter.created_at;
                        const isTodayEncounter = displayDate && isToday(displayDate);
                        
                        return (
                            <div
                                key={encounter.id}
                                onClick={() => navigate(`/pet/${petId}/encounter/${encounter.id}`)}
                                className={`
                                    bg-white dark:bg-dark-bg-secondary rounded-none p-6 
                                    transition-all duration-200 cursor-pointer
                                    hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.01]
                                    ${isTodayEncounter 
                                        ? 'border-2 border-blue-500 dark:border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900/30 shadow-sm hover:ring-4 hover:border-blue-600 dark:hover:border-blue-300' 
                                        : 'border border-gray-200 dark:border-dark-border hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                                    }
                                `}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        {/* Date/Time with Today badge */}
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="flex items-center space-x-2">
                                                <Calendar className={`w-4 h-4 ${isTodayEncounter ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                                                <span className={`text-sm font-medium ${isTodayEncounter ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-dark-text-primary'}`}>
                                                    {formatDate(displayDate)}
                                                </span>
                                            </div>
                                            {isTodayEncounter && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
                                                    <Clock className="w-3 h-3" />
                                                    Today
                                                </span>
                                            )}
                                        </div>

                                        {/* Pet chip - only show if we have pet name */}
                                        {encounter.pet_name && (
                                            <div className="mb-3">
                                                <Link
                                                    to={`/pets/${encounter.pet_id}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="inline-flex items-center px-3 py-2 text-sm font-semibold border-2 rounded-none bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-400 dark:border-blue-600 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                                >
                                                    {encounter.pet_name}
                                                </Link>
                                            </div>
                                        )}

                                        {encounter.intake_data?.chief_complaint && (
                                            <p className="text-gray-900 dark:text-dark-text-primary font-medium mb-1">
                                                {encounter.intake_data.chief_complaint}
                                            </p>
                                        )}

                                        {encounter.assessment_data?.diagnosis && (
                                            <p className="text-gray-600 dark:text-dark-text-muted text-sm mb-3">
                                                Diagnosis: {encounter.assessment_data.diagnosis}
                                            </p>
                                        )}

                                    </div>

                                    {encounter.assigned_vet_name && (
                                        <div className="ml-4 flex-shrink-0" title={encounter.assigned_vet_name}>
                                            <UserAvatar
                                                photoUrl={encounter.assigned_vet_photo_url}
                                                fullName={encounter.assigned_vet_name}
                                                size="md"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {maxItems && encounters.length === maxItems && (
                        <div className="text-center py-4">
                            <Link
                                to={`/pets/${petId}/encounters`} // Assuming we have a route for this
                                className="text-blue-600 hover:text-blue-500 font-medium text-sm"
                            >
                                View all encounters →
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EncountersList;
